/**
 * V2Track's Manual Flight Watch (MB's R184, decision 997, Q880, 3 Nov 2026; docs/specs/tracker.md §5.11). The crew
 * start and stop flight watch on the tracking hardware in the aircraft, and V2Track logs it in the trip as a status
 * word, like Take Off and Landing: "Manual Flight Watch Start" (`ev` 60) and "Manual Flight Watch Stop" (`ev` 61) on
 * VH-JOZ's genuine trips of 2 to 4 Nov 2026 (tests/fixtures/tracking/v2track/trips-flightwatch-vhjoz.live-2026-11-04.txt).
 *
 * Two findings, for the aircraft whose register says "Uses V2Track flight watch" (`aircraft.uses_flight_watch`):
 * - **not started**: airborne (Teslyn's flight state, `flightStatus`: the speed, the tracker's words and the landing
 *   evidence) for the set minutes with flight watch off: no Start since the take-off's last Stop or the tracker's
 *   power-up. A Stop in flight (VH-JOZ's crew stop it on the approach) starts the clock again from that Stop.
 * - **not stopped**: landed (the same flight state) for the set minutes after a flight that had flight watch on, and
 *   no Stop since. The tracker powered down on the ground does not stop it (the watch is V2Track's, not the
 *   tracker's), a new power-up does.
 * A Start or Stop that comes twice (trip 562885436: Stops at 02:06:42 in flight and 02:11:27 on the ground) keeps the
 * first: flight watch went off at the first Stop.
 *
 * Pure rules: the cloud tracker poller (`tracker-poll.ts`, `alert-notices.ts` `flightWatchWordNotices`) and Teslyn
 * Field's map bar (`map-alert-banner.ts`) both use them, so the alert and the bar agree.
 */
import type { AircraftKind } from './aircraft-layer.js';
import { flightStatus } from './flight-status.js';
import { ALERT_QUEUE_MAX_AGE_MS, type LandingStrip, type TrackPoint } from './tracker.js';

/**
 * Default minutes airborne without flight watch before the alert (Tracking settings). VH-JOZ's genuine flights:
 * started 6 minutes after take-off on trip 562905846, and stopped in flight 7 minutes before the landing on trip
 * 562972586, so 5 minutes would have raised two alerts on four normal flights; 10 raises none.
 */
export const FLIGHT_WATCH_NOT_STARTED_MIN = 10;
/**
 * Default minutes landed with flight watch still on before the alert. VH-JOZ's genuine flights stopped it in flight
 * or within 4 minutes of the landing.
 */
export const FLIGHT_WATCH_NOT_STOPPED_MIN = 15;

/** Check a flight watch setting: whole minutes from 1 to 120. */
export function checkFlightWatchMinutes(v: number): string | null {
  return Number.isInteger(v) && v >= 1 && v <= 120 ? null : 'Enter whole minutes from 1 to 120.';
}

/** The tenant's two flight watch minutes; null or a bad value: the default. */
export function flightWatchMinutes(
  s: {
    readonly flight_watch_start_minutes?: number | null;
    readonly flight_watch_stop_minutes?: number | null;
  } | null,
): { readonly notStartedMin: number; readonly notStoppedMin: number } {
  const ok = (v: number | null | undefined, d: number): number =>
    typeof v === 'number' && checkFlightWatchMinutes(v) === null ? v : d;
  return {
    notStartedMin: ok(s?.flight_watch_start_minutes, FLIGHT_WATCH_NOT_STARTED_MIN),
    notStoppedMin: ok(s?.flight_watch_stop_minutes, FLIGHT_WATCH_NOT_STOPPED_MIN),
  };
}

export type FlightWatchWord = 'start' | 'stop';

/**
 * A trip point's flight watch word: "Manual Flight Watch Start" or "... Stop" (genuine), and FlightSpan's documented
 * "Manual FlightWatch Activated" or "... Canceled", without case, spaces or punctuation; null for any other word.
 */
export function flightWatchWord(event: string | null | undefined): FlightWatchWord | null {
  const w = (event ?? '').toLowerCase().replace(/[^a-z]+/g, '');
  if (!w.includes('flightwatch')) return null;
  if (/start|activat/.test(w)) return 'start';
  if (/stop|cancel/.test(w)) return 'stop';
  return null;
}

const isPowerUp = (event: string | null | undefined): boolean =>
  (event ?? '').toLowerCase().replace(/[^a-z]+/g, '') === 'trackerpowerup';

/** Whether a trail holds a flight watch word: the tracker logs flight watch (the register's hint). */
export function logsFlightWatch(points: readonly Pick<TrackPoint, 'event'>[]): boolean {
  return points.some((p) => flightWatchWord(p.event) !== null);
}

export interface FlightWatchState {
  /** Started and not stopped since (nor the tracker powered up since). */
  readonly on: boolean;
  /**
   * When the state began: the first Start while on; while off, the first Stop since, or the tracker's power-up;
   * null when off since before the trail.
   */
  readonly sinceMs: number | null;
  /** The latest Start since the tracker's last power-up (null: none). */
  readonly startMs: number | null;
  /** The latest Stop since the tracker's last power-up (null: none). */
  readonly stopMs: number | null;
}

/** Flight watch at `atMs` from the trail's words at or before it (any order). */
export function flightWatchState(
  points: readonly Pick<TrackPoint, 'timeMs' | 'event'>[],
  atMs: number,
): FlightWatchState {
  let on = false;
  let sinceMs: number | null = null;
  let startMs: number | null = null;
  let stopMs: number | null = null;
  const sorted = points.filter((p) => p.timeMs <= atMs).sort((a, b) => a.timeMs - b.timeMs);
  for (const p of sorted) {
    if (isPowerUp(p.event)) {
      on = false;
      sinceMs = p.timeMs;
      startMs = null;
      stopMs = null;
      continue;
    }
    const w = flightWatchWord(p.event);
    if (w === 'start') {
      if (!on) sinceMs = p.timeMs;
      on = true;
      startMs = p.timeMs;
    } else if (w === 'stop') {
      if (on || sinceMs === null) sinceMs = p.timeMs;
      on = false;
      stopMs = p.timeMs;
    }
  }
  return { on, sinceMs, startMs, stopMs };
}

/** The two minutes of the tenant's Tracking settings. */
export interface FlightWatchMinutes {
  readonly notStartedMin: number;
  readonly notStoppedMin: number;
}

export type FlightWatchFinding =
  | {
      readonly kind: 'not_started';
      /** When the aircraft was airborne with flight watch off: the take-off, or a Stop or power-up after it. */
      readonly sinceMs: number;
      /** When the alert is due: `sinceMs` plus the minutes. */
      readonly dueMs: number;
      readonly takeoffMs: number;
      /** The take-off was not seen (first seen airborne). */
      readonly approximate: boolean;
      /** A Stop after the take-off, when that is what turned it off (null: never started). */
      readonly stoppedMs: number | null;
    }
  | {
      readonly kind: 'not_stopped';
      readonly landingMs: number;
      readonly dueMs: number;
      readonly takeoffMs: number;
      /** The Start that is still on. */
      readonly startMs: number;
    };

/**
 * What the trail says at `nowMs` for an aircraft that uses V2Track flight watch: "not started" or "not stopped", or
 * null (all is well, the minutes have not passed, no flight in the trail, a ground vehicle, or the moment the alert
 * was due is more than 2 hours past, decision 445). The flight is `flightStatus`'s last flight of the trail (with
 * the project's flight-plan strips for the landing evidence, decision 949). `maxAgeMs` (2 hours) is how long after
 * its due moment a finding is still raised; whether an open alert still holds is asked with no limit (Infinity).
 */
export function flightWatchFinding(
  points: readonly TrackPoint[],
  kind: AircraftKind,
  nowMs: number,
  minutes: FlightWatchMinutes,
  strips: readonly LandingStrip[] = [],
  maxAgeMs: number = ALERT_QUEUE_MAX_AGE_MS,
): FlightWatchFinding | null {
  if (kind === 'ground') return null;
  const pts = points.filter((p) => p.timeMs <= nowMs);
  const st = flightStatus(pts, kind, nowMs, strips);
  if (!st) return null;
  const fw = flightWatchState(pts, nowMs);
  const late = (dueMs: number): boolean => nowMs < dueMs || nowMs - dueMs > maxAgeMs;
  if (st.landingMs === null) {
    if (fw.on) return null;
    const sinceMs = Math.max(st.takeoffMs, fw.sinceMs ?? -Infinity);
    const dueMs = sinceMs + minutes.notStartedMin * 60_000;
    if (late(dueMs)) return null;
    return {
      kind: 'not_started',
      sinceMs,
      dueMs,
      takeoffMs: st.takeoffMs,
      approximate: st.approximate,
      stoppedMs: fw.stopMs !== null && fw.stopMs >= st.takeoffMs ? fw.stopMs : null,
    };
  }
  // landed: still on from before the landing (a Start after it is for the next flight)
  if (!fw.on || fw.startMs === null || fw.startMs > st.landingMs) return null;
  const dueMs = st.landingMs + minutes.notStoppedMin * 60_000;
  if (late(dueMs)) return null;
  return {
    kind: 'not_stopped',
    landingMs: st.landingMs,
    dueMs,
    takeoffMs: st.takeoffMs,
    startMs: fw.sinceMs ?? fw.startMs,
  };
}

/** The register's tick for one aircraft: whether its trackers are watched (only a V2Track tracker can be). */
export interface FlightWatchRegisterAircraft {
  readonly id: string;
  readonly registration: string;
  readonly uses_flight_watch?: boolean | null;
  readonly deleted_at?: string | null;
}

/** A tracker asset as the rule needs it. */
export interface FlightWatchTrackerRow {
  readonly provider: string;
  readonly registration: string;
  readonly aircraft_id: string | null;
  readonly deleted_at: string | null;
}

const reg = (r: string | null | undefined): string => (r ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * The aircraft of a tracker: the one it is linked to on the Aircraft page, or, linked to none, the one with its
 * registration (as the overdue flight watch finds an aircraft's trackers).
 */
export function trackerAircraft<A extends FlightWatchRegisterAircraft>(
  t: Pick<FlightWatchTrackerRow, 'registration' | 'aircraft_id'>,
  aircraft: readonly A[],
): A | null {
  const live = aircraft.filter((a) => !a.deleted_at);
  if (t.aircraft_id) return live.find((a) => a.id === t.aircraft_id) ?? null;
  const r = reg(t.registration);
  return r ? (live.find((a) => reg(a.registration) === r) ?? null) : null;
}

/** Whether an aircraft has a V2Track tracker (the tick can be set only then). */
export function hasV2TrackTracker(
  a: Pick<FlightWatchRegisterAircraft, 'id' | 'registration'>,
  trackers: readonly FlightWatchTrackerRow[],
): boolean {
  return trackers.some(
    (t) =>
      t.provider === 'v2track' &&
      !t.deleted_at &&
      (t.aircraft_id === a.id ||
        (!t.aircraft_id && reg(t.registration) !== '' && reg(t.registration) === reg(a.registration))),
  );
}

/** Whether a tracker is watched: a V2Track tracker of an aircraft ticked "Uses V2Track flight watch". */
export function trackerUsesFlightWatch(
  t: FlightWatchTrackerRow,
  aircraft: readonly FlightWatchRegisterAircraft[],
): boolean {
  if (t.provider !== 'v2track' || t.deleted_at) return false;
  return trackerAircraft(t, aircraft)?.uses_flight_watch === true;
}

/** The register's hint for an aircraft whose tracker logs flight watch while the tick is off. */
export const FLIGHT_WATCH_HINT =
  "This aircraft's tracker logs flight watch; tick 'Uses V2Track flight watch' to be alerted";

/** The words of a finding for the map bar and the alerts: "Airborne without flight watch: VH-JOZ". */
export const FLIGHT_WATCH_FINDING_WORD: Readonly<Record<FlightWatchFinding['kind'], string>> = {
  not_started: 'Airborne without flight watch',
  not_stopped: 'Flight watch still on after landing',
};

/**
 * The register's hint for one aircraft (the coordinator's rule, 4 Nov 2026): its tick is off and one of its V2Track
 * trackers has been seen to log a flight watch word (`seen`: `provider|account|device` keys of the cloud's
 * `tracker_flight_watch`). No alert comes of it; the hint says how to have one.
 */
export function flightWatchHint(
  a: FlightWatchRegisterAircraft,
  trackers: readonly (FlightWatchTrackerRow & {
    readonly account_label: string;
    readonly device_id: string;
  })[],
  seen: ReadonlySet<string>,
): string | null {
  if (a.uses_flight_watch === true) return null;
  const logs = trackers.some(
    (t) =>
      t.provider === 'v2track' &&
      !t.deleted_at &&
      seen.has(`${t.provider}|${t.account_label}|${t.device_id}`) &&
      trackerAircraft(t, [a])?.id === a.id,
  );
  return logs ? FLIGHT_WATCH_HINT : null;
}
